import { ProjectPayload, ProjectState } from "hooks/useProjects/useProjects.types";

/**
 * Union type for project data that can be in either API format or transformed format
 */
export type ProjectData = ProjectPayload | ProjectState;

/**
 * Type guard to check if project data is in API format (snake_case)
 */
export function isApiProject(project: ProjectData): project is ProjectPayload {
  return "project_id" in project;
}

/**
 * Type guard to check if project data is in transformed format (camelCase)
 */
export function isTransformedProject(project: ProjectData): project is ProjectState {
  return "projectId" in project;
}

/**
 * Extract project ID from either format
 */
export function getProjectId(project: ProjectData): string {
  if (isApiProject(project)) {
    return project.project_id;
  }
  return project.projectId;
}

/**
 * Backend may return either `versions` (from list with include_versions) or `documents` (from findOne/create).
 * Prefer active_document_id, then versions[0], then documents[0] for documentId.
 */
export function getDocumentId(project: ProjectData): string | undefined {
  if (isApiProject(project)) {
    return (
      project.active_document_id ??
      project.versions?.[0]?.document_id ??
      (project as ApiProjectWithDocuments).documents?.[0]?.document_id
    );
  }
  return (
    project.activeDocumentId ??
    project.versions?.[0]?.documentId ??
    (project as TransformedProjectWithDocuments).documents?.[0]?.documentId
  );
}

interface ApiProjectWithDocuments {
  documents?: Array<{ document_id: string }>;
}

interface TransformedProjectWithDocuments {
  documents?: Array<{ documentId: string }>;
}
